import { QueryKey } from '@tanstack/react-query'

import type { MutationOptions, OperationData } from 'core/types/makeMutationHook'
import type { QueryHookArguments } from 'core/types/makeQueryHook'
import type * as operations from 'core/types/operations'

type GetQuery<Data> =
  Data extends OperationData<infer Resp, infer Key extends QueryKey, infer Vars> ? QueryHookArguments<Resp, Key, Vars>
  : never

type GetMutation<Data> =
  Data extends OperationData<infer Resp, unknown, infer Vars> ? MutationOptions<Resp, Vars> : never

const defaultOptions: {
  [K in keyof typeof operations]?: Partial<
    (typeof operations)[K] extends { method: 'get' } ? GetQuery<(typeof operations)[K]>
    : GetMutation<(typeof operations)[K]>
  >
} = {
  getCaseById: {
    query: { associations: 'all', include: ['dispute', 'purchase'] },
  },

  internalUnboundGetCaseById: {
    query: { associations: 'all', include: ['dispute', 'purchase'] },
  },

  listCasesByBorrower: {
    query: { associations: 'all', include: ['dispute', 'purchase'] },
  },

  loansGet: {
    query: { status: ['all'], includeAdditionalBorrowers: true },
  },

  loanClose: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  loanFreeze: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  loanUnfreeze: {
    options: {
      onSuccess: (_, { personId, body }, { queryClient }) =>
        body?.previewMode ? undefined : queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  loanScheduleDefer: {
    options: {
      onSuccess: async (_, { personId, loanId, body }, { queryClient }) => {
        if (body?.previewMode) return
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
          queryClient.invalidateQueries({ queryKey: ['borrower', 'people', personId, 'loans', loanId] }),
        ])
      },
    },
  },

  loanChargeOffReverse: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  loanUpdateCreditLimit: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  drawUpdateCreditLimit: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] }),
    },
  },

  loanDrawAmortize: {
    options: {
      onSuccess: (_, { personId, body }, { queryClient }) => {
        if (!body?.previewMode) void queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans'] })
        return true
      },
    },
  },

  cancelPromiseToPayPlan: {
    options: {
      onSuccess: (_, { personId, loanId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans', loanId, 'promise-to-pay-plans'] }),
    },
  },

  loanCustomPaymentPlanCancel: {
    options: {
      onSuccess: (_, { personId, loanId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'loans', loanId, 'custom-payment-plans'] }),
    },
  },

  loanRefundsCancel: {
    options: {
      onSuccess: (_, { companyId, personId, loanId }, { queryClient }) =>
        queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'people', personId, 'loans', loanId, 'refunds'],
        }),
    },
  },

  loanTypeBundleAdd: {
    options: {
      // The generated key ends in `'bundle'`, so the hook's own invalidation misses the
      // loan types list this create adds a row to.
      onSuccess: (_response, { companyId }, { queryClient }) => {
        void queryClient.invalidateQueries({ queryKey: ['companies', companyId, 'loan-types'] })
      },
    },
  },

  internalLoanRefundsV2Post: {
    options: {
      onSuccess: (_, { companyId, personId, loanId }, { queryClient }) =>
        queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'people', personId, 'loans', loanId, 'refunds'],
        }),
    },
  },

  updateWorkflowWorkitem: {
    options: {
      onSuccess: (_, { companyId, personId, caseId }, { queryClient }) =>
        queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'people', personId, 'cases', caseId, 'workflow'],
        }),
    },
  },

  updateWorkflowStep: {
    options: {
      onSuccess: (_, { companyId, personId, caseId }, { queryClient }) =>
        queryClient.invalidateQueries({
          queryKey: ['companies', companyId, 'people', personId, 'cases', caseId, 'workflow'],
        }),
    },
  },

  doNotInteractCreate: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'do-not-interacts'] }),
    },
  },

  doNotInteractDelete: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'do-not-interacts'] }),
    },
  },

  doNotInteractUpdate: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'do-not-interacts'] }),
    },
  },

  supercaseBulkDeleteDni: {
    options: {
      onSuccess: (_, { supercaseId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['supercases', supercaseId, 'bulk-operations'] }),
    },
  },

  draftPopulationCommit: {
    options: {
      onSuccess: (_, { supercaseId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['supercases', supercaseId, 'populations'] }),
    },
  },

  draftPopulationMembersSet: {
    options: {
      onSuccess: (_, { supercaseId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['supercases', supercaseId, 'populations', 'draft'] }),
    },
  },

  draftPopulationMembersSetCsv: {
    options: {
      onSuccess: (_, { supercaseId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['supercases', supercaseId, 'populations', 'draft'] }),
    },
  },

  snoozeCase: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'cases'] }),
    },
  },

  caseEscalationsCreate: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'cases'] }),
    },
  },

  interactionAdd: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) => {
        setTimeout(() => queryClient.invalidateQueries({ queryKey: ['people', personId, 'interactions'] }), 0)
      },
    },
  },

  interactionDelete: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) => {
        void queryClient.invalidateQueries({ queryKey: ['people', personId, 'interactions'] })
        void queryClient.invalidateQueries({ queryKey: ['people', personId, 'interaction-notes'] })
      },
    },
  },

  handleSend: {
    options: {
      onSuccess: (_, { body }, { queryClient }) => {
        if (body?.personId) {
          void queryClient.invalidateQueries({ queryKey: ['people', body.personId, 'interactions'] })
        }
      },
    },
  },

  addCaseAssociation: {
    options: {
      onSuccess: (_, { personId, interactionId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'interactions', interactionId, 'cases'] }),
    },
  },

  removeCaseAssociation: {
    options: {
      onSuccess: (_, { personId, interactionId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'interactions', interactionId, 'cases'] }),
    },
  },

  updateInteractionNote: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'interaction-notes'] }),
    },
  },

  createInteractionNote: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) =>
        queryClient.invalidateQueries({ queryKey: ['people', personId, 'interaction-notes'] }),
    },
  },

  unboundInteractionUpdate: {
    options: {
      onSuccess: (_, { interactionId, body }, { queryClient }) => {
        if (body?.personId) {
          void queryClient.invalidateQueries({ queryKey: ['people', body.personId, 'interactions'] })
        }
        void queryClient.invalidateQueries({ queryKey: ['interactions', interactionId] })
      },
    },
  },

  updateIdentity: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) => {
        void queryClient.invalidateQueries({ queryKey: ['people', personId, 'identity'] })
      },
    },
  },

  updateTasktypeAvailability: {
    options: {
      onSuccess: (_, _vars, { queryClient }) => {
        void queryClient.invalidateQueries({ queryKey: ['employees'] })
      },
    },
  },

  deleteIdentity: {
    options: {
      onSuccess: (_, { personId }, { queryClient }) => {
        void queryClient.invalidateQueries({ queryKey: ['people', personId, 'identity'] })
      },
    },
  },
}

const getDefaultOptions = <Resp, Key extends QueryKey, Vars>(
  data: OperationData<Resp, Key, Vars>,
): Partial<typeof data extends { method: 'get' } ? QueryHookArguments<Resp, Key, Vars> : MutationOptions<Resp, Vars>> =>
  defaultOptions[data.name]

export default getDefaultOptions
